import { Box, CircularProgress, Typography, Snackbar, Alert } from "@mui/material";
import React, { useEffect, useState } from "react";
import api from "../../api/axiosInstance";
import TransactionsList from "./TransactionsList";
import TransactionScreenHeader from "./TransactionScreenHeader";
import { TransactionListProps } from "../../typings/transactions";
import { PaginationProps } from "../../typings";
import { PAGINATION_VALUES } from "../../data/pagination";

const Transactions = () => {
  const [transactions, setTransactions] = useState<TransactionListProps[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationProps>(PAGINATION_VALUES);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message: string, severity: "success" | "error" | "info" | "warning") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const fetchTransactions = async (showLoadingSpinner = true) => {
    try {
      if (showLoadingSpinner) {
        setLoading(true);
      }
      setError("");
      
      const response = await api.get("/accounts/transactions/list/", {
        params: {
          page_no: pagination.page_number,
          page_size: pagination.page_size,
        },
      });

      if (response.data && response.data.data) {
        setTransactions(response.data.data);
        
        // Update pagination with server response
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            total_no_of_records: response.data.pagination.total_no_of_records || prev.total_no_of_records,
            total_no_of_pages: response.data.pagination.total_no_of_pages || prev.total_no_of_pages,
          }));
        }
      } else {
        setTransactions([]);
        showSnackbar("No transactions found", "info");
      }
    } catch (error: any) {
      console.error("Error fetching transactions:", error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.detail || 
                          error.message || 
                          "Failed to fetch transactions. Please try again.";
                          
      setError(errorMessage);
      showSnackbar(errorMessage, "error");
      
      // Set empty transactions on error
      setTransactions([]);
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [pagination.page_number, pagination.page_size]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPagination((p) => ({ ...p, page_number: newPage + 1 })); // MUI is 0-based
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination({
      page_number: 1,
      page_size: parseInt(event.target.value, 10),
      total_no_of_records: pagination.total_no_of_records,
      total_no_of_pages: pagination.total_no_of_pages,
    });
  };

  const handleRefresh = async () => {
    await fetchTransactions(false); // Don't show loading spinner for refresh
    if (!error) {
      showSnackbar("Transactions refreshed successfully", "success");
    }
  };

  const handleTransactionCreated = () => {
    // Refresh the transactions list when a new transaction is created
    handleRefresh();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <TransactionScreenHeader onTransactionCreated={handleTransactionCreated} />
      
      {error && !loading && (
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="error" 
            onClose={() => setError("")}
            sx={{ borderRadius: 2 }}
          >
            {error}
          </Alert>
        </Box>
      )}
      
      <TransactionsList 
        transactions={transactions} 
        pagination={pagination}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onRefresh={handleRefresh}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Transactions;
